import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../utils/api';

export default function LoginPage() {
  const { login, register, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'artist'
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Email verification state
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      // Check if user needs to verify email
      if (detail.toLowerCase().includes('verify') || detail.toLowerCase().includes('pending')) {
        setVerifyEmail(loginData.email);
        setShowVerifyEmail(true);
        toast.error('Please verify your email first');
      } else {
        toast.error(detail);
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.full_name,
        registerData.role
      );
      toast.success('Account created! Please check your email for verification code.');
      setVerifyEmail(registerData.email);
      setShowVerifyEmail(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { token: verificationCode });
      toast.success('Email verified successfully! You can now login.');
      setShowVerifyEmail(false);
      setVerificationCode('');
      setActiveTab('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setVerifyLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: verifyEmail });
      toast.success('Verification code resent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowResetForm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success('Password reset successfully! You can now login.');
      setShowForgotPassword(false);
      setShowResetForm(false);
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-32 bg-[#1A1412] flex items-center justify-center" data-testid="login-page">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/qzpuppc8_IMG_8052.jpeg" 
            alt="Vintage Ave Records"
            className="h-auto w-full max-w-sm mx-auto mb-4 rounded-xl shadow-2xl"
          />
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7] tracking-tight">
            Welcome to Vintage Ave
          </h1>
        </div>

        <Card className="bg-[#2C221F] border-[#3D302C]">
          {showVerifyEmail ? (
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7]">
                  Verify Your Email
                </h2>
                <p className="text-[#A89B95] text-sm mt-2">
                  We've sent a verification code to <span className="text-[#D97736]">{verifyEmail}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyEmail} className="space-y-4" data-testid="verify-email-form">
                <div>
                  <Label htmlFor="verify-code" className="text-[#FDFBF7]">Verification Code</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                    placeholder="Enter code from email"
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="verify-code-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                  disabled={verifyLoading}
                  data-testid="verify-submit-button"
                >
                  {verifyLoading ? 'Verifying...' : 'Verify Email'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button 
                  type="button"
                  onClick={handleResendVerification}
                  disabled={verifyLoading}
                  className="text-[#D97736] hover:text-[#F08F4D] text-sm"
                  data-testid="resend-verification-button"
                >
                  Didn't receive the code? Resend
                </button>
              </div>

              <button 
                onClick={() => {
                  setShowVerifyEmail(false);
                  setVerificationCode('');
                }}
                className="w-full mt-4 text-[#A89B95] hover:text-[#FDFBF7] text-sm"
                data-testid="back-to-login-from-verify"
              >
                Back to Login
              </button>
            </CardContent>
          ) : showForgotPassword ? (
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7]">
                  {showResetForm ? 'Reset Password' : 'Forgot Password'}
                </h2>
                <p className="text-[#A89B95] text-sm mt-2">
                  {showResetForm 
                    ? 'Enter the reset code from your email and your new password.' 
                    : 'Enter your email to receive a password reset code.'}
                </p>
              </div>

              {!showResetForm ? (
                <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
                  <div>
                    <Label htmlFor="forgot-email" className="text-[#FDFBF7]">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                    disabled={forgotLoading}
                    data-testid="forgot-submit-button"
                  >
                    {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
                  <div>
                    <Label htmlFor="reset-token" className="text-[#FDFBF7]">Reset Code</Label>
                    <Input
                      id="reset-token"
                      type="text"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      required
                      placeholder="Enter code from email"
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      data-testid="reset-token-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-password" className="text-[#FDFBF7]">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-[#FDFBF7]">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                    disabled={forgotLoading}
                    data-testid="reset-submit-button"
                  >
                    {forgotLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              )}

              <button 
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowResetForm(false);
                  setResetToken('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-full mt-4 text-[#D97736] hover:text-[#F08F4D] text-sm"
                data-testid="back-to-login-button"
              >
                Back to Login
              </button>
            </CardContent>
          ) : (
          <>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 bg-[#1A1412]">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
                  data-testid="login-tab"
                >
                  Login
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="data-[state=active]:bg-[#D97736] data-[state=active]:text-white"
                  data-testid="register-tab"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {activeTab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email" className="text-[#FDFBF7]">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-[#FDFBF7]">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="login-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                  data-testid="login-submit-button"
                >
                  Login
                </Button>
                <button 
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="w-full text-[#D97736] hover:text-[#F08F4D] text-sm mt-2"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
                
                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#3D302C]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-[#2C221F] text-[#A89B95]">or</span>
                  </div>
                </div>
                
                {/* Google Login Button */}
                <Button 
                  type="button"
                  onClick={loginWithGoogle}
                  className="w-full bg-white hover:bg-gray-100 text-gray-800 rounded-full flex items-center justify-center gap-2"
                  data-testid="google-login-button"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
                    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="register-name" className="text-[#FDFBF7]">Full Name</Label>
                  <Input
                    id="register-name"
                    value={registerData.full_name}
                    onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-[#FDFBF7]">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-[#FDFBF7]">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="bg-black/20 border-white/10 text-[#FDFBF7] focus:border-[#D97736]"
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-role" className="text-[#FDFBF7]">I am a...</Label>
                  <select
                    id="register-role"
                    value={registerData.role}
                    onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 text-[#FDFBF7] rounded-md px-3 py-2 focus:border-[#D97736] focus:outline-none"
                    data-testid="register-role-select"
                  >
                    <option value="artist">Artist</option>
                    <option value="model">Model</option>
                  </select>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                  data-testid="register-submit-button"
                >
                  Sign Up
                </Button>
                
                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#3D302C]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-[#2C221F] text-[#A89B95]">or</span>
                  </div>
                </div>
                
                {/* Google Sign Up Button */}
                <Button 
                  type="button"
                  onClick={loginWithGoogle}
                  className="w-full bg-white hover:bg-gray-100 text-gray-800 rounded-full flex items-center justify-center gap-2"
                  data-testid="google-signup-button"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
                    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
                  </svg>
                  Sign up with Google
                </Button>
              </form>
            )}
          </CardContent>
          </>
          )}
        </Card>
      </div>
    </div>
  );
}